import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ZapIcon, ShieldIcon, CodeIcon, DollarSignIcon, UsersIcon, LayoutGridIcon, SparklesIcon, LockIcon, TrendingUpIcon, CheckCircleIcon, ArrowRightIcon, GlobeIcon, CreditCardIcon, RocketIcon, FolderIcon, ShareIcon, WrenchIcon } from 'lucide-react';
type Tab = 'discover' | 'build' | 'use';
export const Marketing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('discover');
  const stats = [{
    label: 'Active Apps',
    value: '10,000+'
  }, {
    label: 'Developers',
    value: '50,000+'
  }, {
    label: 'Monthly Users',
    value: '2M+'
  }, {
    label: 'AI Models',
    value: '15+'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-1373">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600" data-id="element-1374">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-1375" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-24" data-id="element-1376">
          <div className="text-center" data-id="element-1377">
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-1378">
              The AI App Platform
              <br data-id="element-1379" />
              <span className="text-cyan-200" data-id="element-1380">for Everyone</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-1381">
              Discover, build, and use AI applications without the complexity.
              One platform, unlimited possibilities.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12" data-id="element-1382">
              {stats.map(stat => <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-1383">
                  <div className="text-3xl font-bold text-white mb-1" data-id="element-1384">
                    {stat.value}
                  </div>
                  <div className="text-sm text-blue-100" data-id="element-1385">{stat.label}</div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm" data-id="element-1386">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1387">
          <div className="flex justify-center space-x-1 py-4" data-id="element-1388">
            {[{
            id: 'discover',
            label: 'Discover Apps',
            icon: SearchIcon
          }, {
            id: 'build',
            label: 'Build Apps',
            icon: CodeIcon
          }, {
            id: 'use',
            label: 'Use Apps',
            icon: ZapIcon
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as Tab)} className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1389">
                <tab.icon className="h-5 w-5" data-id="element-1390" />
                <span data-id="element-1391">{tab.label}</span>
              </button>)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-16" data-id="element-1392">
        {/* Discover Tab */}
        {activeTab === 'discover' && <div className="space-y-20" data-id="element-1393">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1394">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6" data-id="element-1395">
                <SearchIcon className="h-8 w-8 text-blue-600" data-id="element-1396" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1397">
                Explore Thousands of AI Apps
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1398">
                From image generation to code optimization, find the perfect AI
                tool for any task. No setup required.
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1399">
                Browse App Store
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1400" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1401">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1402">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1403">
                  <LayoutGridIcon className="h-6 w-6 text-blue-600" data-id="element-1404" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1405">
                  Vast Repository
                </h3>
                <p className="text-gray-600" data-id="element-1406">
                  Access 10,000+ AI applications across every category. From
                  productivity to creativity, we have the tools you need.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1407">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1408">
                  <CreditCardIcon className="h-6 w-6 text-green-600" data-id="element-1409" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1410">
                  One Credit System
                </h3>
                <p className="text-gray-600" data-id="element-1411">
                  Use credits across all apps. No more juggling multiple
                  accounts, subscriptions, or API keys for different AI
                  services.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1412">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1413">
                  <RocketIcon className="h-6 w-6 text-purple-600" data-id="element-1414" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1415">
                  Instant Access
                </h3>
                <p className="text-gray-600" data-id="element-1416">
                  Start using any app immediately. No complex setup, no
                  configuration. Just click and create.
                </p>
              </div>
            </div>

            {/* Visual Showcase */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-12 text-white" data-id="element-1417">
              <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1418">
                <div data-id="element-1419">
                  <h3 className="text-3xl font-bold mb-4" data-id="element-1420">
                    Discover Your Next Favorite Tool
                  </h3>
                  <ul className="space-y-4" data-id="element-1421">
                    <li className="flex items-start" data-id="element-1422">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1423" />
                      <span className="text-blue-50" data-id="element-1424">
                        Browse by category or search for specific capabilities
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1425">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1426" />
                      <span className="text-blue-50" data-id="element-1427">
                        Read reviews and ratings from real users
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1428">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1429" />
                      <span className="text-blue-50" data-id="element-1430">
                        Try apps instantly with our unified credit system
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1431">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1432" />
                      <span className="text-blue-50" data-id="element-1433">
                        Save favorites to your personal library
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20" data-id="element-1434">
                  <div className="space-y-3" data-id="element-1435">
                    <div className="h-12 bg-white/20 rounded-lg animate-pulse" data-id="element-1436" />
                    <div className="grid grid-cols-3 gap-3" data-id="element-1437">
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1438" />
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1439" />
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1440" />
                    </div>
                    <div className="h-32 bg-white/20 rounded-lg animate-pulse" data-id="element-1441" />
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Build Tab */}
        {activeTab === 'build' && <div className="space-y-20" data-id="element-1442">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1443">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-6" data-id="element-1444">
                <CodeIcon className="h-8 w-8 text-green-600" data-id="element-1445" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1446">
                Build & Monetize AI Apps in Minutes
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1447">
                No code required. Use our AI-powered builder to create, deploy,
                and monetize applications instantly.
              </p>
              <button onClick={() => navigate('/create')} className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1448">
                Start Building
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1449" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1450">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1451">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1452">
                  <SparklesIcon className="h-6 w-6 text-blue-600" data-id="element-1453" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1454">
                  AI-Powered Builder
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1455">
                  Describe your app idea in plain English. Our AI helps you
                  build it through a conversational interface.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1456">
                  <li className="flex items-center" data-id="element-1457">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1458" />
                    Chat-based app creation
                  </li>
                  <li className="flex items-center" data-id="element-1459">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1460" />
                    Instant preview and testing
                  </li>
                  <li className="flex items-center" data-id="element-1461">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1462" />
                    One-click deployment
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1463">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1464">
                  <GlobeIcon className="h-6 w-6 text-purple-600" data-id="element-1465" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1466">
                  Choose Any AI Model
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1467">
                  Plug into GPT-4, Claude, Gemini, or any other AI model. Switch
                  between models with a single click.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1468">
                  <li className="flex items-center" data-id="element-1469">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1470" />
                    15+ AI models supported
                  </li>
                  <li className="flex items-center" data-id="element-1471">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1472" />
                    Easy model comparison
                  </li>
                  <li className="flex items-center" data-id="element-1473">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1474" />
                    Automatic fallbacks
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1475">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1476">
                  <DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-1477" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1478">
                  Built-in Monetization
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1479">
                  Set your price and start earning. We handle payments,
                  distribution, and analytics.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1480">
                  <li className="flex items-center" data-id="element-1481">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1482" />
                    Flexible pricing models
                  </li>
                  <li className="flex items-center" data-id="element-1483">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1484" />
                    Instant payouts
                  </li>
                  <li className="flex items-center" data-id="element-1485">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1486" />
                    Revenue analytics
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1487">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1488">
                  <WrenchIcon className="h-6 w-6 text-orange-600" data-id="element-1489" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1490">
                  No Code Required
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1491">
                  Build professional apps without writing a single line of code.
                  Simple forms and visual tools.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1492">
                  <li className="flex items-center" data-id="element-1493">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1494" />
                    Visual app builder
                  </li>
                  <li className="flex items-center" data-id="element-1495">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1496" />
                    Pre-built templates
                  </li>
                  <li className="flex items-center" data-id="element-1497">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1498" />
                    Easy API integration
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-12 text-white text-center" data-id="element-1499">
              <h3 className="text-3xl font-bold mb-4" data-id="element-1500">
                Join 50,000+ Developers Building on RealUseAI
              </h3>
              <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto" data-id="element-1501">
                From idea to published app in minutes. Start building your AI
                business today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1502">
                <button onClick={() => navigate('/create')} className="px-8 py-4 bg-white text-green-600 rounded-xl font-semibold hover:bg-green-50 transition-colors shadow-lg" data-id="element-1503">
                  Create Your First App
                </button>
                <button onClick={() => navigate('/apps')} className="px-8 py-4 bg-green-700 text-white rounded-xl font-semibold hover:bg-green-800 transition-colors border-2 border-white/20" data-id="element-1504">
                  View Examples
                </button>
              </div>
            </div>
          </div>}

        {/* Use Tab */}
        {activeTab === 'use' && <div className="space-y-20" data-id="element-1505">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1506">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6" data-id="element-1507">
                <ZapIcon className="h-8 w-8 text-purple-600" data-id="element-1508" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1509">
                Your Personal AI Workspace
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1510">
                Build custom workflows, organize your favorite apps, and keep
                your data secure. All in one place.
              </p>
              <button onClick={() => navigate('/my-apps')} className="inline-flex items-center px-8 py-4 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1511">
                Go to My Apps
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1512" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1513">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1514">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1515">
                  <FolderIcon className="h-6 w-6 text-purple-600" data-id="element-1516" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1517">
                  Personal Library
                </h3>
                <p className="text-gray-600" data-id="element-1518">
                  Save and organize your favorite apps. Access them instantly
                  from your personal dashboard.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1519">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1520">
                  <WrenchIcon className="h-6 w-6 text-blue-600" data-id="element-1521" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1522">
                  Custom Workflows
                </h3>
                <p className="text-gray-600" data-id="element-1523">
                  Build apps tailored to your specific needs. Modify existing
                  apps or create new ones from scratch.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1524">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1525">
                  <ShareIcon className="h-6 w-6 text-green-600" data-id="element-1526" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1527">
                  Share & Collaborate
                </h3>
                <p className="text-gray-600" data-id="element-1528">
                  Share your custom apps with team members or the community.
                  Collaborate on workflows.
                </p>
              </div>
            </div>

            {/* Privacy Section */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-white" data-id="element-1529">
              <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1530">
                <div data-id="element-1531">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl mb-6" data-id="element-1532">
                    <ShieldIcon className="h-6 w-6 text-white" data-id="element-1533" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4" data-id="element-1534">
                    Your Data, Your Control
                  </h3>
                  <p className="text-slate-300 mb-6" data-id="element-1535">
                    We take privacy seriously. Your data is encrypted,
                    protected, and never shared without your permission.
                  </p>
                  <ul className="space-y-3" data-id="element-1536">
                    <li className="flex items-start" data-id="element-1537">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1538" />
                      <span className="text-slate-200" data-id="element-1539">
                        End-to-end encryption for all data
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1540">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1541" />
                      <span className="text-slate-200" data-id="element-1542">
                        Private apps stay private by default
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1543">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1544" />
                      <span className="text-slate-200" data-id="element-1545">
                        Full control over data sharing and permissions
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1546">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-1547" />
                      <span className="text-slate-200" data-id="element-1548">
                        GDPR and SOC 2 compliant
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10" data-id="element-1549">
                  <div className="space-y-4" data-id="element-1550">
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1551">
                      <span className="text-sm" data-id="element-1552">Data Encryption</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1553" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1554">
                      <span className="text-sm" data-id="element-1555">Private by Default</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1556" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1557">
                      <span className="text-sm" data-id="element-1558">Access Controls</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1559" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-1560">
                      <span className="text-sm" data-id="element-1561">Compliance Ready</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1562" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1563">
              <div className="space-y-6" data-id="element-1564">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1565">
                  Productivity Features
                </h3>
                <div className="space-y-4" data-id="element-1566">
                  <div className="flex items-start" data-id="element-1567">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1568">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-1569" />
                    </div>
                    <div data-id="element-1570">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1571">
                        Quick Access Dashboard
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1572">
                        All your apps in one place with smart organization
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1573">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1574">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-1575" />
                    </div>
                    <div data-id="element-1576">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1577">
                        Usage History
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1578">
                        Track your interactions and revisit previous work
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1579">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1580">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-1581" />
                    </div>
                    <div data-id="element-1582">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1583">
                        Custom Categories
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1584">
                        Organize apps your way with tags and folders
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-1585">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1586">
                  Customization Options
                </h3>
                <div className="space-y-4" data-id="element-1587">
                  <div className="flex items-start" data-id="element-1588">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1589">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-1590" />
                    </div>
                    <div data-id="element-1591">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1592">
                        Modify Any App
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1593">
                        Fork and customize existing apps to fit your needs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1594">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1595">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-1596" />
                    </div>
                    <div data-id="element-1597">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1598">
                        Build Private Apps
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1599">
                        Create apps just for yourself or your team
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-1600">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1601">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-1602" />
                    </div>
                    <div data-id="element-1603">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1604">
                        Workflow Automation
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-1605">
                        Chain apps together for powerful workflows
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 py-20" data-id="element-1606">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-1607">
          <h2 className="text-4xl font-bold text-white mb-6" data-id="element-1608">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-1609">
            Join millions of users discovering, building, and using AI apps on
            RealUseAI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1610">
            <button onClick={() => navigate('/')} className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg" data-id="element-1611">
              Explore Apps
            </button>
            <button onClick={() => navigate('/create')} className="px-8 py-4 bg-indigo-700 text-white rounded-xl font-semibold hover:bg-indigo-800 transition-colors border-2 border-white/20" data-id="element-1612">
              Start Building
            </button>
          </div>
        </div>
      </div>
    </div>;
};